import {
  Sstv2MigrationPhase,
  Sstv2MigrationProcessingState,
} from "shared/sstv2MigrationData"

/**
 * THE VOCABULARY TABLE — every jargon token this page renders, with the one-line
 * plain-English gloss an operator needs to act on it (DEV-193, D37 pass).
 *
 * ## Why this exists as data rather than as `switch` statements
 *
 * The migration panels display `MATERIALIZING_ACTIVE`, `DRAINING_HISTORY`,
 * `alreadyFlipped` and `adopted` verbatim. Those are backend enum values and
 * internal counter names; an operator reading them during an incident has no way to
 * know that `adopted` is PROGRESS or that `DRAINING_HISTORY` can legitimately run for
 * hours after cutover. Every one of them therefore has a gloss reachable from the
 * screen it appears on, and the glosses live in ONE table so a term cannot pick up a
 * second, divergent explanation somewhere else on the page.
 *
 * ## ⚠ THE EXTENSION POINT — read this before adding a state
 *
 * The `recovery-reachability` work in `apps/sl2/src/**` adds a per-stream fold
 * provenance stamp and a set of new detector outcomes (`completion-proven`,
 * `completion-sealed`, `completion-execution-alive`, `completion-restarted`,
 * `completion-restart-failed`, `completion-budget-exhausted`,
 * `completion-deferred`). None of them exists at `0cd056dbf`, so none is modelled
 * here — but they must be able to ARRIVE without a refactor.
 *
 * The shape that makes that work, and which must be preserved:
 *
 *  1. Each table is an EXHAUSTIVE `Record<ClosedUnion, Sstv2TermDef>`. Dropping the
 *     gloss for a value that exists today is a compile error, not a blank tooltip.
 *  2. Every LOOKUP is tolerant — `describe*` takes a `string`, not the union, and
 *     falls through to `unknownTerm(raw)`. A value the union does not yet contain
 *     renders as "Unknown" **with its raw value shown**, never throwing, never
 *     blanking a panel, never dropping the row it sits on.
 *
 * So adding the recovery vocabulary is: widen the backend union, add a row to the
 * matching table below, add two locale strings. A DATA CHANGE. Do not replace the
 * tolerant lookup with an index into the union — that is the refactor this shape
 * exists to prevent, and it converts an unmodelled value from "visibly unknown" back
 * into "silently mislabelled", which is how the D5 documentation stayed wrong.
 */

export type Sstv2TermDef = {
  /** i18n key for the SHORT label — what goes in the chip or the row label. */
  labelKey: string
  /** i18n key for the one-line plain-English gloss shown in the tooltip. */
  glossKey: string
  /**
   * Decision id (D-something) or register issue backing this term, when there is
   * one. Rendered as a plain suffix so a curious operator can search the
   * architecture doc for the long form — the long form is deliberately NOT pasted
   * into the UI.
   */
  decision?: string
}

export type Sstv2Term = Sstv2TermDef & {
  /**
   * False when the raw value is not one this UI models — i.e. the backend moved
   * ahead of the frontend. Callers MUST render `raw` alongside the label in that
   * case, so the screen says "we do not know this state, and here is what it is"
   * rather than inventing a plausible one.
   */
  known: boolean
  /** The raw backend value. Always carried, known or not. */
  raw: string
}

const UNKNOWN_TERM: Sstv2TermDef = {
  labelKey: "migrationTermUnknown",
  glossKey: "migrationTermUnknownGloss",
}

/**
 * The fallback. Deliberately distinct from every "nothing has happened yet" term:
 * "not started" is a fact about the migration, "unknown" is a fact about THIS UI,
 * and conflating them is how a newly-shipped backend state reads as an idle locker.
 */
export const unknownTerm = (raw: string): Sstv2Term => ({
  ...UNKNOWN_TERM,
  known: false,
  raw,
})

const resolve = <K extends string>(
  table: Record<K, Sstv2TermDef>,
  raw: string | undefined,
  absent: Sstv2Term,
): Sstv2Term => {
  if (raw === undefined || raw === null || raw === "") return absent
  const def = (table as Record<string, Sstv2TermDef | undefined>)[raw]
  return def ? { ...def, known: true, raw } : unknownTerm(raw)
}

// ---------------------------------------------------------------------------
// Partner PHASE — what the PARTNER has told us, on their ladder
// ---------------------------------------------------------------------------

/**
 * ⚠ `phase` and `processingState` are DIFFERENT AXES and the page renders both.
 * `phase` is derived from the partner's events; `processingState` is our own
 * pipeline. They can and do disagree — most confusingly, partner `COMPLETED` means
 * "the partner finished SENDING the history", while our `COMPLETE` means "we
 * finished WRITING both streams". An operator who reads the first as the second
 * concludes a migration is finished while the history flip is still running.
 */
const PHASE_TERMS: Record<Sstv2MigrationPhase, Sstv2TermDef> = {
  ACTIVE_MIGRATED: {
    labelKey: "migrationPhaseActiveMigrated",
    glossKey: "migrationPhaseActiveMigratedGloss",
    decision: "D15",
  },
  OPERATIONAL: {
    labelKey: "migrationPhaseOperational",
    glossKey: "migrationPhaseOperationalGloss",
    decision: "D13",
  },
  COMPLETED: {
    labelKey: "migrationPhaseCompleted",
    glossKey: "migrationPhaseCompletedGloss",
  },
  ROLLBACK_STARTED: {
    labelKey: "migrationPhaseRollbackStarted",
    glossKey: "migrationPhaseRollbackStartedGloss",
  },
  ROLLED_BACK: {
    labelKey: "migrationPhaseRolledBack",
    glossKey: "migrationPhaseRolledBackGloss",
    decision: "D6",
  },
}

const PHASE_NOT_STARTED: Sstv2Term = {
  labelKey: "migrationPhaseNotStarted",
  glossKey: "migrationPhaseNotStartedGloss",
  known: true,
  raw: "NOT_STARTED",
}

export const describePhase = (raw?: string): Sstv2Term =>
  raw === "NOT_STARTED"
    ? PHASE_NOT_STARTED
    : resolve(PHASE_TERMS, raw, PHASE_NOT_STARTED)

// ---------------------------------------------------------------------------
// OUR processingState — what WE have actually done
// ---------------------------------------------------------------------------

/**
 * `MATERIALIZING_ACTIVE` keeps its name because it is the backend enum value; the
 * *label* carries the correct verb ("flipping"), since nothing is materialized any
 * more. `DRAINING_HISTORY` is the state D37 made load-bearing: before D37 history was
 * never walked, so this state was a formality. It is now a real drain over the
 * locker's entire lifetime of reservations and can outlast cutover by a long way.
 */
const PROCESSING_STATE_TERMS: Record<
  Sstv2MigrationProcessingState,
  Sstv2TermDef
> = {
  INGESTING: {
    labelKey: "migrationProcessingIngesting",
    glossKey: "migrationProcessingIngestingGloss",
    decision: "D15",
  },
  MATERIALIZING_ACTIVE: {
    labelKey: "migrationProcessingMaterializingActive",
    glossKey: "migrationProcessingMaterializingActiveGloss",
    decision: "D2",
  },
  OPERATIONAL: {
    labelKey: "migrationProcessingOperational",
    glossKey: "migrationProcessingOperationalGloss",
    decision: "D3",
  },
  DRAINING_HISTORY: {
    labelKey: "migrationProcessingDrainingHistory",
    glossKey: "migrationProcessingDrainingHistoryGloss",
    decision: "D37",
  },
  COMPLETE: {
    labelKey: "migrationProcessingComplete",
    glossKey: "migrationProcessingCompleteGloss",
  },
  ROLLBACK_STARTED: {
    labelKey: "migrationProcessingRollbackStarted",
    glossKey: "migrationProcessingRollbackStartedGloss",
  },
  ROLLED_BACK: {
    labelKey: "migrationProcessingRolledBack",
    glossKey: "migrationProcessingRolledBackGloss",
    decision: "D6",
  },
}

const PROCESSING_NOT_STARTED: Sstv2Term = {
  labelKey: "migrationProcessingUnknown",
  glossKey: "migrationProcessingNotStartedGloss",
  known: true,
  raw: "",
}

/**
 * ⚠ An ABSENT state and an UNRECOGNISED one are different answers.
 *
 * Until 2026-07-31 both landed on `migrationProcessingUnknown`, whose English reads
 * "Not started" — so the first `processingState` the backend ships that this build
 * does not know would render as an idle attempt. That is the exact failure mode the
 * sibling's new vocabulary would have walked into.
 */
export const describeProcessingState = (raw?: string): Sstv2Term =>
  resolve(PROCESSING_STATE_TERMS, raw, PROCESSING_NOT_STARTED)

// ---------------------------------------------------------------------------
// Flip execution verdicts
// ---------------------------------------------------------------------------

/**
 * Verdicts are computed in `./sstv2AttemptState`, so today's set is closed by
 * construction. The lookup is tolerant anyway: the recovery work adds outcomes that
 * arrive as BACKEND-supplied strings rather than locally-derived ones, and the day
 * one of those reaches this table is the day a closed index would throw inside a
 * render.
 */
const FLIP_VERDICT_TERMS: Record<string, Sstv2TermDef> = {
  "not-configured": {
    labelKey: "migrationFlipNotConfigured",
    glossKey: "migrationFlipNotConfiguredHint",
  },
  running: {
    labelKey: "migrationFlipRunning",
    glossKey: "migrationFlipRunningHint",
    decision: "D13",
  },
  "rolling-back": {
    labelKey: "migrationFlipRollingBack",
    glossKey: "migrationFlipRollingBackHint",
  },
  stalled: {
    labelKey: "migrationFlipStalled",
    glossKey: "migrationFlipStalledHint",
  },
  done: {
    labelKey: "migrationFlipDone",
    glossKey: "migrationFlipDoneHint",
  },
  "not-started": {
    labelKey: "migrationFlipNotStarted",
    glossKey: "migrationFlipNotStartedHint",
  },
  "rolled-back": {
    labelKey: "migrationFlipRolledBack",
    glossKey: "migrationFlipRolledBackHint",
    decision: "D6",
  },
}

export const describeFlipVerdict = (raw: string): Sstv2Term =>
  resolve(FLIP_VERDICT_TERMS, raw, unknownTerm(raw))

// ---------------------------------------------------------------------------
// The per-stream counters
// ---------------------------------------------------------------------------

/**
 * The five numbers a stream reports. `adopted` is the one that matters most here:
 * it reliably reads as a failure to anyone seeing it for the first time, and it is
 * the opposite — a reservation that reached V2 by the only route available to it.
 */
export const STREAM_COUNTER_KEYS = [
  "received",
  "processed",
  "adopted",
  "failed",
  "alreadyFlipped",
  // Added 2026-07-31 when the fold-provenance stamp landed — via the extension point
  // above, exactly as designed: widen the list, add a row, add two locale strings.
  "foldedCount",
] as const

export type Sstv2StreamCounterKey = (typeof STREAM_COUNTER_KEYS)[number]

const STREAM_COUNTER_TERMS: Record<Sstv2StreamCounterKey, Sstv2TermDef> = {
  received: {
    labelKey: "migrationCounterReceived",
    glossKey: "migrationCounterReceivedGloss",
    decision: "D15",
  },
  processed: {
    labelKey: "migrationCounterProcessed",
    glossKey: "migrationCounterProcessedGloss",
    decision: "D2",
  },
  adopted: {
    labelKey: "migrationCounterAdopted",
    glossKey: "migrationCounterAdoptedGloss",
    decision: "D10",
  },
  failed: {
    labelKey: "migrationCounterFailed",
    glossKey: "migrationCounterFailedGloss",
  },
  alreadyFlipped: {
    labelKey: "migrationCounterAlreadyFlipped",
    glossKey: "migrationCounterAlreadyFlippedGloss",
    decision: "D4",
  },
  foldedCount: {
    labelKey: "migrationCounterFolded",
    glossKey: "migrationCounterFoldedGloss",
    decision: "D13",
  },
}

export const describeStreamCounter = (raw: string): Sstv2Term =>
  resolve(STREAM_COUNTER_TERMS, raw, unknownTerm(raw))

// ---------------------------------------------------------------------------
// Fold provenance — "who certified these numbers"
// ---------------------------------------------------------------------------

/**
 * The two provenance states of a stream, and the reason they earn their own vocabulary
 * rather than reusing the counter glosses.
 *
 * Every other term here explains a NUMBER. These explain the difference between a
 * number and a fact. A stream can sit at 100% with balanced counters and still have
 * been certified by nothing — that is migration `ace1c2c7…`, whose history counters
 * balanced because ingestion credited them at write-down while nothing ever walked the
 * partition. An operator looking at that bar had no way to tell, and the tooling
 * agreed with the bar.
 *
 * `unproven` is therefore NOT styled as a failure. It is the ordinary state of a stream
 * that has not been walked yet and of every attempt older than the stamp. Only
 * `unproven` NEXT TO a COMPLETE processingState is a problem, and the callout — not
 * this chip — is what says so.
 */
export const STREAM_PROVENANCE_KEYS = ["proven", "unproven"] as const

export type Sstv2StreamProvenanceKey = (typeof STREAM_PROVENANCE_KEYS)[number]

const STREAM_PROVENANCE_TERMS: Record<
  Sstv2StreamProvenanceKey,
  Sstv2TermDef
> = {
  proven: {
    labelKey: "migrationProvenanceProven",
    glossKey: "migrationProvenanceProvenGloss",
    decision: "D13",
  },
  unproven: {
    labelKey: "migrationProvenanceUnproven",
    glossKey: "migrationProvenanceUnprovenGloss",
    decision: "D13",
  },
}

export const describeStreamProvenance = (raw: string): Sstv2Term =>
  resolve(STREAM_PROVENANCE_TERMS, raw, unknownTerm(raw))

// ---------------------------------------------------------------------------
// The two streams
// ---------------------------------------------------------------------------

/**
 * ⚠ D37 (2026-07-31, superseding D5). Both streams get the IDENTICAL treatment —
 * same page loop, same bulk read, same conditional write, same undo record. They
 * differ in two ways only, and both are things an operator needs told:
 *
 *  - **ORDER.** `fnSstv2MigrationFlip` walks ACTIVE first, then HISTORY
 *    (`FLIP_STREAMS`). Active reservations are what a courier can be blocked on now;
 *    history is a background drain.
 *  - **WHEN THEY FINISH.** Each stream has its own arrival gate and its own fold, so
 *    they complete independently. Active typically finishes at cutover; history can
 *    still be running long afterwards, because it is the locker's whole lifetime.
 */
export const STREAM_KEYS = ["active", "history"] as const
export type Sstv2StreamKey = (typeof STREAM_KEYS)[number]

const STREAM_TERMS: Record<Sstv2StreamKey, Sstv2TermDef> = {
  active: {
    labelKey: "migrationActiveStream",
    glossKey: "migrationActiveStreamGloss",
    decision: "D37",
  },
  history: {
    labelKey: "migrationHistoryStream",
    glossKey: "migrationHistoryStreamGloss",
    decision: "D37",
  },
}

export const describeStream = (raw: string): Sstv2Term =>
  resolve(STREAM_TERMS, raw, unknownTerm(raw))
